import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { AccessibilityWrapper } from 'legacy-packages/ft-cb';
import { STUDENT_TEST_BOOK } from 'legacy/src/constants/assessmentResourceTypes';
import { assessmentTestPreviewQuery } from 'legacy-src/queries';
import { loadingPopover as LoadingPDFPopover } from '../DownloadButton/paperscorer';
import { getSignedS3ResourceUrl } from 'chameleon-src/utility/signedUrl';
import SingleAction from 'chameleon/ui-stack/ui/assessments/actions/single-action';
import log from 'chameleon/ui-stack/utilities/log';
import Button from 'chameleon/ui-stack/ui/base/button';

import './styles.scss';

const btnProps = { color: 'gray' };
const printBtnText = 'Print Preview';

/**
 * Print Preview button for previewing a QB.
 * PE Assessment: STUDENT_TEST_BOOK_PREVIEW resource is downloaded
 * Other QB Assessment: TEST BOOKLET is generated by Learnosity and downloaded.
 * */
const PrintPreview = (props) => {
  const {
    client,
    item,
    masterSubjectId,
    autoSave: { shouldAutoSave, autoSaveHandler },
    showAttestationFlag,
    showIcons,
    downloadFreshCopy,
    onDownload,
    label,
    className,
    role,
    onSendTelemetryEvent,
  } = props;
  const { associated_resources: associatedResources = {} } = item;
  // FY-23951 show the original testbook
  const previewResource = associatedResources[STUDENT_TEST_BOOK];
  const [state, setState] = useState({ loading: false });
  const [autoDownloadLearnosityPDF, setAutoDownloadLearnosityPDF] =
    useState(false);
  const { loading, resourceUrl } = state;

  useEffect(() => {
    if (autoDownloadLearnosityPDF) {
      getLearnosityPdf();
    }
  }, [autoDownloadLearnosityPDF]);

  const downloadInNewTab = (url) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    link.target = '_blank';
    link.dispatchEvent(new MouseEvent('click'));
  };

  const downloadInSameTab = (url) => {
    window.location.assign(url);
  };

  /**
   * Calls API to generate test booklet PDF on Learnosity
   * Receives back the URL of the document.
   * */
  const getLearnosityPdf = async () => {
    onSendTelemetryEvent && onSendTelemetryEvent();
    setAutoDownloadLearnosityPDF(false);
    setState({
      loading: true,
    });

    client
      .query({
        query: assessmentTestPreviewQuery,
        fetchPolicy: 'network-only',
        variables: { assessmentId: item.item_id, masterSubjectId },
      })
      .then((resp) => {
        const url = resp.data.assessment.previewPdfUrl;
        downloadInSameTab(url);
        onDownload(downloadFreshCopy);

        setState({
          loading: false,
          resourceUrl: url,
        });
      })
      .catch((error) => {
        setState({
          loading: false,
        });

        log('error', error);
      });
  };

  const getSignedPdf = async () => {
    if (previewResource) {
      setState({
        loading: true,
      });

      const signedResourceUrl = await getSignedS3ResourceUrl(previewResource);
      downloadInNewTab(signedResourceUrl);
      onDownload(downloadFreshCopy);
      onSendTelemetryEvent && onSendTelemetryEvent();

      setState({
        loading: false,
        resourceUrl: signedResourceUrl,
      });
    }
  };

  /*
   * Call the parent auto save handler.
   * Sets the state to auto-download PDF so next time it re-renders the PDF will be
   * automatically downloaded.
   * */
  const autoSaveQuiz = async () => {
    await autoSaveHandler();
    setAutoDownloadLearnosityPDF(true);
  };

  if (!resourceUrl || shouldAutoSave || downloadFreshCopy) {
    if (showAttestationFlag && previewResource) {
      return (
        <div className="pdf-loader-popover__container qb-print-preview-popover__container">
          {loading && <LoadingPDFPopover showIcons={showIcons} />}
          <AccessibilityWrapper>
            {(showIcons && (
              <SingleAction
                role={role}
                onClick={getSignedPdf}
                disabled={loading}
                tabIndex={0}
                iconName="print"
                iconWidth="16"
                iconHeight="16"
                title="Print Preview"
                ariaLabel="Print Preview"
              >
                {label}
              </SingleAction>
            )) || (
              <Button
                isSecondary
                tabIndex={0}
                className={className}
                onClick={getSignedPdf}
                disabled={loading}
              >
                {printBtnText}
              </Button>
            )}
          </AccessibilityWrapper>
        </div>
      );
    }

    return (
      <div className="pdf-loader-popover__container qb-print-preview-popover__container">
        {loading && <LoadingPDFPopover showIcons={showIcons} />}
        <AccessibilityWrapper>
          {(showIcons && (
            <SingleAction
              role={role}
              className={cn('preview-icon-container', className)}
              onClick={shouldAutoSave ? autoSaveQuiz : getLearnosityPdf}
              disabled={loading}
              tabIndex={0}
              iconName="print"
              iconWidth="16"
              iconHeight="16"
              title="Print Preview"
              ariaLabel="Print Preview"
            >
              {label}
            </SingleAction>
          )) || (
            <Button
              isSecondary
              tabIndex={0}
              className={className}
              onClick={shouldAutoSave ? autoSaveQuiz : getLearnosityPdf}
              btn={btnProps}
              disabled={loading}
            >
              {printBtnText}
            </Button>
          )}
        </AccessibilityWrapper>
      </div>
    );
  }

  return (
    <AccessibilityWrapper>
      <SingleAction
        role={role}
        className={cn(className, {
          'preview-icon-container': showIcons,
          print_preview_link: !showIcons,
        })}
        href={resourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={0}
        iconName="print"
        title="Print Preview"
        ariaLabel="Print Preview"
      >
        {showIcons ? label : printBtnText}
      </SingleAction>
    </AccessibilityWrapper>
  );
};

PrintPreview.displayName = 'PrintPreview';

PrintPreview.propTypes = {
  // The graphQL client for querying the test booklet.
  client: PropTypes.shape({
    query: PropTypes.func,
  }).isRequired,
  // The item we want to preview the PDF for.
  item: PropTypes.shape({
    item_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      .isRequired,
    associated_resources: PropTypes.object,
  }).isRequired,
  // Feature flag for PE functionality
  showAttestationFlag: PropTypes.bool,
  // The ID of the master subject.
  masterSubjectId: PropTypes.string.isRequired,
  // The auto-save handler for saving an updated quiz in order to generate the right PDF.
  autoSave: PropTypes.shape({
    shouldAutoSave: PropTypes.bool,
    autoSaveHandler: PropTypes.func,
  }),
  downloadFreshCopy: PropTypes.bool,
  onDownload: PropTypes.func,
  label: PropTypes.any,
  role: PropTypes.string,
  onSendTelemetryEvent: PropTypes.func,
};

PrintPreview.defaultProps = {
  onSendTelemetryEvent: () => {},
  item: {
    item_id: null,
    associated_resources: {},
  },
  showAttestationFlag: false,
  autoSave: {
    shouldAutoSave: false,
    autoSaveHandler: () => {},
  },
  showIcons: false,
  downloadFreshCopy: false,
  onDownload: () => {},
  label: '',
  role: 'button',
};

export default PrintPreview;
